import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card } from "@/components/ui/Primitives";
import { isDemoOn, setDemo, DEMO_META } from "@/lib/demoMask";
import { api, formatErr } from "@/lib/api";
import { Camera, CheckCircle2, AlertTriangle, Eye, EyeOff, Video, Users, Building2, Truck, FileDown } from "lucide-react";

export default function ModoDemo() {
  const nav = useNavigate();
  const [on, setOn] = useState(isDemoOn());

  useEffect(() => {
    const h = () => setOn(isDemoOn());
    window.addEventListener("demo-mode-change", h);
    return () => window.removeEventListener("demo-mode-change", h);
  }, []);

  const toggle = () => {
    setDemo(!on);
    setTimeout(() => window.location.reload(), 300);
  };

  return (
    <Layout
      title="Modo Demonstração"
      subtitle="Grave vídeos e prints do sistema sem expor dados reais"
      actions={<Button variant="secondary" onClick={() => nav("/settings")}>Voltar</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Card principal — switch */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${on ? "bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white" : "bg-stone-100 text-ink-500"}`}>
              <Camera size={26} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading text-lg font-bold text-ink-900">Modo Demonstração</h3>
                {on ? (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="demo-status-on">Ligado</span>
                ) : (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-stone-100 text-ink-500 border border-ink-200" data-testid="demo-status-off">Desligado</span>
                )}
              </div>
              <p className="text-sm text-ink-500 mb-4">
                Ative antes de gravar vídeos, tirar screenshots ou demonstrar o sistema pra prospects. Nenhum dado no banco é alterado — apenas a exibição na tela.
              </p>
              <Button
                onClick={toggle}
                variant={on ? "secondary" : "primary"}
                data-testid="demo-toggle-btn"
              >
                {on ? <><EyeOff size={16} /> Desligar Modo Demo</> : <><Eye size={16} /> Ativar Modo Demo</>}
              </Button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-tertiary">
            <h4 className="font-heading font-bold text-ink-900 mb-3 text-sm">O que muda com o Modo ativo:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-stone-50 border border-ink-200 flex items-start gap-2">
                <Building2 size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-ink-900">Empresa</div>
                  <div className="text-xs text-ink-500">→ <span className="font-mono">{DEMO_META.nome_fantasia}</span></div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-stone-50 border border-ink-200 flex items-start gap-2">
                <Users size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-ink-900">Clientes</div>
                  <div className="text-xs text-ink-500">→ Rede Mercado Verde, Fazenda São Bento, etc</div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-stone-50 border border-ink-200 flex items-start gap-2">
                <Truck size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-ink-900">Fornecedores</div>
                  <div className="text-xs text-ink-500">→ Fazenda Aurora, Sítio Vista Alegre, etc</div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-stone-50 border border-ink-200 flex items-start gap-2">
                <CheckCircle2 size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-ink-900">CNPJs & Placas</div>
                  <div className="text-xs text-ink-500">→ Mascarados com valores fictícios</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-xs">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900">
              <strong>Atenção:</strong> Este modo é <strong>local ao seu navegador</strong>. Se você abrir o sistema em outro dispositivo, ele estará desligado por padrão. Uma faixa colorida no topo aparece sempre que o modo estiver ativo, pra você não esquecer de desligar antes de operar com dados reais.
            </div>
          </div>
        </Card>

        {/* Guia de uso rápido */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Video size={18} className="text-brand-500" />
            <h3 className="font-heading font-bold text-ink-900">Fluxo pra gravar</h3>
          </div>
          <ol className="space-y-3 text-sm text-ink-700">
            <li className="flex gap-2">
              <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
              <span>Ative o Modo Demo aqui</span>
            </li>
            <li className="flex gap-2">
              <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
              <span>Confirme a faixa lilás no topo (indica que está ativo)</span>
            </li>
            <li className="flex gap-2">
              <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
              <span>Grave/screenshote as telas normalmente</span>
            </li>
            <li className="flex gap-2">
              <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">4</span>
              <span>Desligue o modo ao terminar</span>
            </li>
          </ol>

          <div className="mt-4 pt-4 border-t border-tertiary">
            <h4 className="font-heading font-bold text-ink-900 text-sm mb-2">📄 Apresentação Comercial</h4>
            <p className="text-xs text-ink-500 mb-3">
              PDF de 8 páginas com identidade Dataneres pra você enviar às parceiras.
            </p>
            <Button
              variant="secondary"
              className="w-full"
              onClick={async () => {
                try {
                  const res = await api.get("/marketing/pitch-pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "dataneres_apresentacao_comercial.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) { alert(formatErr(e)); }
              }}
              data-testid="download-pitch-pdf-btn"
            >
              <FileDown size={16} /> Baixar PDF Dataneres
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
